import { AlertCircle, CheckCircle, CreditCard, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface PaymentSetupStatusBannerProps {
  jobId: string;
  clientComplete: boolean;
  vendorComplete: boolean;
  bothComplete: boolean;
  userRole: 'client' | 'vendor' | null;
  hasAnyMilestoneApproval?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function PaymentSetupStatusBanner({
  jobId,
  clientComplete,
  vendorComplete,
  bothComplete,
  userRole,
  hasAnyMilestoneApproval = false,
  onRefresh,
  isRefreshing = false,
}: PaymentSetupStatusBannerProps) {
  const navigate = useNavigate();

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  // Don't show the "Payment Setup Complete" banner if milestone planning has started
  // (i.e., at least one party has approved)
  if (bothComplete && hasAnyMilestoneApproval) {
    return null;
  }

  if (bothComplete) {
    return (
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-900">Payment Setup Complete</AlertTitle>
        <AlertDescription className="text-green-700 flex items-center justify-between gap-4">
          <span>Both parties have completed payment setup. You can now proceed to milestone planning.</span>
          <Button
            size="sm"
            onClick={() => navigate(`/jobs/${jobId}/milestones`)}
            className="shrink-0 bg-green-600 hover:bg-green-700 text-white"
          >
            Go to Milestones
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const needsAction = 
    (userRole === 'client' && !clientComplete) || 
    (userRole === 'vendor' && !vendorComplete);

  if (!needsAction) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Waiting for Payment Setup</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            {userRole === 'client' 
              ? 'Waiting for vendor to complete Stripe Connect setup.'
              : 'Waiting for client to verify payment method.'}
          </span>
          {onRefresh && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRefresh}
              disabled={isRefreshing}
              className="shrink-0"
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
              Refresh Status
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Payment Setup Required</AlertTitle>
      <AlertDescription className="flex items-start justify-between gap-4">
        <span>
          {userRole === 'client'
            ? 'You need to verify your payment method before proceeding to milestone planning.'
            : 'You need to connect your Stripe account to receive payments for this job.'}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/jobs/${jobId}/payment-setup`)}
          className="shrink-0"
        >
          <CreditCard className="h-4 w-4 mr-2" />
          Complete Setup
        </Button>
      </AlertDescription>
    </Alert>
  );
}
